import { logExceptionOnce } from '@hungryroot/o11y';
import {
  type GetCustomerExperimentsResponse,
  type ExperimentsResponse,
} from '@hungryroot/rest';
import { type UseCustomerExperimentsType } from '../types';
import {
  useExperimentDefinitions,
  ExperimentDefinitionsApiError,
} from './useExperimentDefinitions';

declare global {
  interface Window {
    [key: `exp_${number}`]: ExperimentVariantValue | undefined;
  }
}

type ExperimentVariantValue =
  GetCustomerExperimentsResponse['results'][number]['variant_modulo'];

type RequiredExperimentsType = Exclude<
  UseCustomerExperimentsType['data'],
  undefined
>;

function convertWindowExperimentsToCanonicalNumber({
  results: experiments,
}: ExperimentsResponse): RequiredExperimentsType {
  const bucketing: Record<
    string,
    RequiredExperimentsType['experiments'][number]
  > = {};
  experiments.forEach((experiment) => {
    const { is_pre_signup: isPreSignUp, id: experimentId } = experiment;
    if (isPreSignUp === false) {
      return;
    }

    const windowProperty = `exp_${Math.floor(experimentId / 100)}` as const;
    const value = window[windowProperty] as unknown;
    if (typeof value !== 'number') {
      return;
    }

    const bucketingValue: RequiredExperimentsType['experiments'][number] = {
      experiment: experimentId,
      variant: value,
      variant_modulo: (value % 10) as ExperimentVariantValue,
    };
    const existing = bucketing[windowProperty];
    if (existing === undefined) {
      bucketing[windowProperty] = bucketingValue;
      return;
    }

    const { experiment: existingExperimentNumber } = existing;
    if (existingExperimentNumber < experimentId) {
      bucketing[windowProperty] = bucketingValue;
    }
  });

  return {
    experiments: Object.values(bucketing),
  };
}

type Params = {
  enabled: boolean;
};

export function useAnonymousWindowExperiments({ enabled }: Params) {
  const { data: experimentDefinitionsData, error: experimentDefinitionsError } =
    useExperimentDefinitions(
      {},
      {
        enabled,
      }
    );

  if (experimentDefinitionsError) {
    if (experimentDefinitionsError instanceof ExperimentDefinitionsApiError) {
      logExceptionOnce(
        experimentDefinitionsError,
        { key: 'useAnonymousWindowExperiments.error' },
        {
          responseData: experimentDefinitionsError.data,
          responseStatus: experimentDefinitionsError.status,
        }
      );
    }

    return {
      isLoading: false,
      error: experimentDefinitionsError,
      isError: true,
      data: undefined,
    };
  }

  if (experimentDefinitionsData === undefined) {
    return {
      isLoading: true,
      error: null,
      isError: false,
      data: undefined,
    };
  }

  if (experimentDefinitionsData.results === undefined) {
    logExceptionOnce(
      'Undefined experiment definitions data',
      { key: 'useAnonymousWindowExperiments.data.undefined' },
      {
        experimentDefinitionsData: JSON.parse(
          JSON.stringify(experimentDefinitionsData)
        ),
      }
    );

    return {
      isLoading: false,
      error: null,
      isError: false,
      data: undefined,
    };
  }

  return {
    isLoading: false,
    error: null,
    isError: false,
    data: convertWindowExperimentsToCanonicalNumber(experimentDefinitionsData),
  };
}
